import { Button, Group, Modal, Table, Text, TextInput } from '@mantine/core';
import { IconReceiptRefund } from '@tabler/icons';
import { useEffect, useState } from 'react';
import { PaidBankHour } from '../../@types/bank-hour';
import { useBulkSettleBankHoursData } from '../../data/hooks/bank-hours';
import { useIsMobile } from '../../hooks/useIsMobile';
import { successNotification } from '../../providers/mantine-notifications';
import {
  downloadFile,
  formatBRL,
  formatSignedHours,
  realToUSCash,
} from '../../utils/helpers';

interface PaymentReceiptModalProps {
  /** what was just paid; the modal opens while there is something here */
  payments: PaidBankHour[] | null;
  /** printed on the receipt, ex: "01/04/25 a 30/04/25" */
  period: string;
  onClose: () => void;
}

/**
 * Right after paying, offers the settlement receipt with the EXACT hours that
 * were paid (no retyping). Only the amount in R$ is informed.
 */
export function PaymentReceiptModal({
  payments,
  period,
  onClose,
}: PaymentReceiptModalProps) {
  const isMobile = useIsMobile();
  const [values, setValues] = useState<Record<number, string>>({});
  const { fetch: bulkFetcher, loading } = useBulkSettleBankHoursData();

  useEffect(() => setValues({}), [payments]);

  const filled = (payments ?? []).filter((p) => values[p.userId]?.trim());

  function handleGenerate() {
    bulkFetcher({
      records: filled.map((p) => ({
        username: p.username,
        bhAtual: `${p.hours}:${String(p.minutes).padStart(2, '0')}:00`,
        totalValue: realToUSCash(values[p.userId]),
        period,
      })),
      onSuccess: (response: Blob) => {
        downloadFile(response, `quitacao-banco-horas-${Date.now()}.pdf`);
        successNotification({
          title: 'Recibo gerado com sucesso!',
          message: 'O download do PDF iniciou automaticamente.',
        });
        onClose();
      },
    });
  }

  return (
    <Modal
      opened={!!payments?.length}
      onClose={onClose}
      title="Pagamento concluído"
      size="lg"
      fullScreen={isMobile}
      closeOnClickOutside={false}
    >
      <Text size="sm" mb="md">
        As horas abaixo saíram do saldo. Informe o valor pago para gerar o
        recibo de quitação com essas mesmas horas, ou feche para gerar depois.
      </Text>
      <Table>
        <thead>
          <tr>
            <th>Funcionário</th>
            <th>Horas pagas</th>
            <th>Valor (R$)</th>
          </tr>
        </thead>
        <tbody>
          {(payments ?? []).map((p) => (
            <tr key={p.userId}>
              <td>{p.username}</td>
              <td>{formatSignedHours(p.seconds)}</td>
              <td>
                <TextInput
                  aria-label={`Valor de ${p.username}`}
                  placeholder="R$ 0,00"
                  value={values[p.userId] ?? ''}
                  onChange={(e) =>
                    setValues((prev) => ({
                      ...prev,
                      [p.userId]: formatBRL(e.target.value),
                    }))
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Group position="right" mt="lg">
        <Button variant="default" onClick={onClose} disabled={loading}>
          Agora não
        </Button>
        <Button
          leftIcon={<IconReceiptRefund size={16} />}
          disabled={!filled.length}
          loading={loading}
          onClick={handleGenerate}
        >
          Gerar recibo{filled.length > 1 ? 's' : ''}
        </Button>
      </Group>
    </Modal>
  );
}
